import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractComponent,
  Card,
  CardService,
  CompanyCrmLists,
  CompanyService,
  Contact,
  ContactService,
  CountryModel,
  CustomerContactRoleEnum,
  Customer,
  CustomerContact,
  CustomerService,
  CustomField,
  CustomFieldContact,
  CustomFieldScopeEnum,
  CustomFieldService,
  UtilHelper,
} from 'lib-trend-core';
import { defer, finalize, forkJoin, Observable, of, switchMap, takeUntil } from 'rxjs';

/** Opened from the customer list (`{}` / `{ customer }`) and from the deal detail (`{ customer, card }`). */
export interface LeadModalData {
  customer?: Customer | null;
  /** When present, the deal-level dates (presentation, inclusion) are editable too (spec A-10). */
  card?: Card | null;
}

type AddContactBody = Parameters<CustomerService['addContact']>[1];

/**
 * The customer modal (SPEC-2026-09-crm-lead-rich-registration — RF-01..RF-10, RF-16, RF-18, RF-26, RF-28).
 *
 * One screen, three destinations (spec A-10): the top block is the account's own name plus
 * the primary contact's phone, e-mail and job title; the document and the company block
 * write the account; the dates of *Qualificação* write the deal when the modal was opened
 * from one. The account has no person-or-company kind — a CNPJ only reveals the company
 * fields (owner decision 2026-09-15). Creating by typing lets the API resolve the primary by
 * phone, creating a contact when none exists; picking an existing contact makes it the
 * primary and fills the fields. Linked contacts are picked from the base or typed, and
 * travel with the account when creating.
 */
@Component({
    selector: 'app-customer-form-modal',
    templateUrl: './customer-form-modal.component.html',
    styleUrl: './customer-form-modal.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CustomerFormModalComponent extends AbstractComponent implements OnInit {

  /** The primary contact's own fields in the contact form — written to the contact, not to the account. */
  private static readonly PERSON_FIELDS: ReadonlyArray<string> = ['country', 'phone', 'email', 'contactPosition', 'contactCompany', 'city', 'state'];

  customer: Customer | null = null;
  card: Card | null = null;
  /** The primary contact — the messaging identity (edit mode). */
  contact: Contact | null = null;
  /** An existing contact picked as the primary (create mode). */
  selectedContact: Contact | null = null;
  /** People queued while creating — linked together with the account, in one save (RF-01). */
  pendingContacts: Array<CustomerContact> = [];

  lists: CompanyCrmLists = { segments: [], employeeBands: [] };
  qualificationFields: Array<CustomField> = [];

  contactForm!: FormGroup;
  accountForm!: FormGroup;
  newContactForm!: FormGroup;
  /** The inline "new person" form of the linked contacts is open. */
  addingContact = false;
  /** The base picker of the linked contacts is open. */
  pickingContact = false;
  savingContact = false;

  countries: CountryModel[] = [];
  selectedCountryCode = '';
  phoneMask = '';
  newContactPhoneMask = '';

  /** CPF up to 11 digits; ngx-mask switches to the CNPJ pattern from the 12th digit on. */
  readonly documentMask = '000.000.000-00||00.000.000/0000-00';
  readonly zipMask = '00000-000';
  readonly roles = Object.values(CustomerContactRoleEnum);

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<CustomerFormModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LeadModalData,
    private readonly customerService: CustomerService,
    private readonly contactService: ContactService,
    private readonly cardService: CardService,
    private readonly companyService: CompanyService,
    private readonly customFieldService: CustomFieldService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.isNew = !this.data?.customer?._id;
    this.card = this.data?.card ?? null;
    this.countries = UtilHelper.getCountries();

    // The caller's copy until the account read below returns: the modal is usable meanwhile,
    // so a save before the read (or after a failed one) still has the account it edits.
    this.customer = this.data?.customer ?? null;
    this.contact = this.contactOf(this.customer);

    this.buildForms(this.customer);
    this.loadLists();
    this.loadQualificationFields();

    // The caller's copy is shallow (the deal populates the account one level deep);
    // the account read resolves the primary contact and the other contacts.
    if (!this.isNew) this.reload(this.data.customer._id);
  }

  /** More digits than a CPF: the company block shows. Nothing else changes — the account has no kind. */
  get hasCnpj(): boolean {
    return this.documentDigits.length > 11;
  }

  /** Which document the digits form so far — the feedback beside the label; empty until something is typed. */
  get documentLabel(): string {
    if (!this.documentDigits) return '';
    return this.hasCnpj ? 'CNPJ' : 'CPF';
  }

  /** Edit: the account's other people, populated. Creation: the people queued to travel with the account. */
  get additionalContacts(): Array<CustomerContact> {
    if (this.isNew) return this.pendingContacts;
    return (this.customer?.contacts ?? []).filter((entry) => entry?.contact && typeof entry.contact === 'object');
  }

  get customFieldsGroup(): FormGroup {
    return this.accountForm.get('customFields') as FormGroup;
  }

  private get documentDigits(): string {
    return `${this.accountForm?.get('document')?.value ?? ''}`.replace(/\D/g, '');
  }

  roleLabel(role: string): string {
    return `kanban.leadModal.role.${role}`;
  }

  // ---------------------------------------------------------------------------
  // Forms
  // ---------------------------------------------------------------------------

  /**
   * Built ONCE, when the modal opens. The account read that follows and a promotion only
   * patch what they own (`applyReloaded`, `applyPromoted`): rebuilding threw away every
   * unsaved edit, and a save afterwards wrote the stored values back.
   */
  private buildForms(customer: Customer | null): void {
    const contact = this.contactOf(customer);
    const person = this.contactFormValues(customer, contact);
    const account = this.accountValues(customer);

    /**
     * The name is the account's own; everything else in this block belongs to the primary
     * contact and is written there (spec A-01 — a datum about a lead is recorded once).
     * Employer and location are here because the score reads them and no other screen of
     * the CRM offers them (spec §3, first goal; AC-17).
     */
    this.contactForm = this.formBuilder.group({
      name: [person.name, Validators.required],
      country: [null, Validators.required],
      phone: ['', Validators.required],
      email: [person.email],
      contactPosition: [person.contactPosition],
      contactCompany: [person.contactCompany],
      city: [person.city],
      state: [person.state],
    });

    this.accountForm = this.formBuilder.group({
      // The customer's code in the company's ERP, when there is one — kept as given.
      code: [account.code],
      // CPF or CNPJ of the account. Optional and unvalidated (owner decision 2026-09-15): the mask formats it, nothing refuses it.
      document: [account.document],
      legalName: [account.legalName],
      tradeName: [account.tradeName],
      stateRegistration: [account.stateRegistration, [Validators.pattern(/^(\d+|isento)?$/i)]],
      website: [account.website],
      instagram: [account.instagram],
      address: this.formBuilder.group(account.address),
      segment: [account.segment],
      employeeBand: [account.employeeBand],
      // Deal-level dates (spec A-10) — only saved when the modal was opened from a deal.
      presentedAt: [this.card?.presentedAt ? new Date(this.card.presentedAt) : null],
      presentedTime: [this.card?.presentedAt ? this.timeOf(new Date(this.card.presentedAt)) : ''],
      includedAt: [this.card?.includedAt ? new Date(this.card.includedAt) : null],
      customFields: this.formBuilder.group({}),
    });

    this.newContactForm = this.formBuilder.group({
      name: ['', Validators.required],
      country: [null, Validators.required],
      phone: ['', Validators.required],
      email: [''],
      contactPosition: [''],
      role: [CustomerContactRoleEnum.OTHER],
    });

    this.applyPhone(this.contactForm, contact?.phone);
    this.syncPhoneRequirement(contact);
    this.bindQualificationFields();
  }

  /** The primary as an object — `customer.contact` is only an id until the account read populates it. */
  private contactOf(customer: Customer | null): Contact | null {
    return customer?.contact && typeof customer.contact === 'object' ? customer.contact : null;
  }

  /** The primary contact's own fields — the block a promotion replaces. */
  private personValues(customer: Customer | null, contact: Contact | null) {
    return {
      email: contact?.email ?? customer?.email ?? '',
      contactPosition: contact?.contactPosition ?? '',
      contactCompany: contact?.contactCompany ?? '',
      city: contact?.city ?? '',
      state: contact?.state ?? '',
    };
  }

  /** The contact form's values: the account's name above the primary's own fields. */
  private contactFormValues(customer: Customer | null, contact: Contact | null) {
    return { name: customer?.name ?? contact?.name ?? '', ...this.personValues(customer, contact) };
  }

  /** The account's own fields — the deal dates in the same form are the deal's, not the account's. */
  private accountValues(customer: Customer | null) {
    const address = customer?.address ?? {};
    return {
      code: customer?.code ?? '',
      document: customer?.tin ?? '',
      legalName: customer?.legalName ?? '',
      tradeName: customer?.tradeName ?? '',
      stateRegistration: customer?.stateRegistration ?? '',
      website: customer?.website ?? '',
      instagram: customer?.instagram ?? '',
      address: {
        zip: address.zip ?? '',
        street: address.street ?? '',
        number: address.number ?? '',
        complement: address.complement ?? '',
        neighborhood: address.neighborhood ?? '',
        city: address.city ?? '',
        state: address.state ?? '',
      },
      segment: customer?.segment ?? '',
      employeeBand: customer?.employeeBand ?? '',
    };
  }

  /** Fills only what the user has not touched yet, recursing into nested groups. */
  private patchPristine(group: FormGroup, values: Record<string, unknown>): void {
    Object.entries(values).forEach(([key, value]) => {
      const control = group.get(key);
      if (control instanceof FormGroup) {
        this.patchPristine(control, (value ?? {}) as Record<string, unknown>);
      } else if (control?.pristine) {
        control.setValue(value);
      }
    });
  }

  /**
   * Shows a person in the primary block as stored — a promoted primary, the one picked when
   * creating, or nobody once the pick is removed. Nothing in the block is an edit to save until
   * the user changes it: what was typed there before stops counting, so only real changes are written.
   */
  private applyPerson(contact: Contact | null, values: Record<string, unknown>): void {
    this.contactForm.patchValue(values);
    this.applyPhone(this.contactForm, contact?.phone);
    this.syncPhoneRequirement(contact);
    CustomerFormModalComponent.PERSON_FIELDS.forEach((key) => this.contactForm.get(key)?.markAsPristine());
  }

  /**
   * Splits a stored phone into the country select and the masked number. A contact may have
   * none (Instagram, Messenger, BSUID): the fields are cleared rather than keeping a number
   * that belongs to nobody on screen.
   */
  private applyPhone(form: FormGroup, phone?: string): void {
    if (!phone) {
      form.patchValue({ country: null, phone: '' });
      return;
    }
    const countryCode = this.extractCountryCode(phone);
    form.patchValue({ country: countryCode || null, phone: this.extractPhoneNumber(phone) });
    if (countryCode) this.applyCountryMask(countryCode, form === this.contactForm ? 'primary' : 'new');
  }

  /**
   * The phone identifies a person typed here, and a primary that has one must keep it. A
   * primary picked or edited without one is resolved by its id and has none to give, so the
   * account is saved without it — otherwise the form could never be submitted for it.
   */
  private syncPhoneRequirement(contact: Contact | null): void {
    const required = !contact || !!contact.phone;
    ['country', 'phone'].forEach((name) => {
      const control = this.contactForm.get(name);
      control?.setValidators(required ? Validators.required : null);
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  onCountryChange(event: Event, target: 'primary' | 'new' = 'primary'): void {
    this.applyCountryMask((event.target as HTMLSelectElement).value, target);
  }

  private applyCountryMask(code: string, target: 'primary' | 'new'): void {
    const selectedCountry = this.countries.find((country) => country.code === code);
    const mask = selectedCountry?.code === '+244'
      ? '000 000 000'
      : selectedCountry?.code === '+55'
        ? '(00) 0000-0000||(00) 00000-0000'
        : selectedCountry?.mask || '';

    if (target === 'primary') {
      this.selectedCountryCode = code;
      this.phoneMask = mask;
    } else {
      this.newContactPhoneMask = mask;
    }
  }

  keyPressOnlyNumbers(event: KeyboardEvent): void {
    const pattern = /[0-9\+\-\ ]/;
    const inputChar = String.fromCharCode(event.charCode);
    if (event.keyCode !== 8 && !pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  // ---------------------------------------------------------------------------
  // Loads
  // ---------------------------------------------------------------------------

  private reload(idCustomer: string): void {
    this.customerService.getById(idCustomer)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (customer: Customer) => this.applyReloaded(customer),
        // The modal stays usable on the caller's copy; the read that failed is the load, not a save.
        error: () => this.alertService.error(this.t('kanban.customerFormModal.loadError')),
      });
  }

  /**
   * The account read that follows the opening — the caller's copy was shallow. The modal is
   * usable meanwhile, so only what the user has not touched yet is filled: nothing typed
   * before the read returns is lost.
   */
  private applyReloaded(customer: Customer): void {
    this.customer = customer;
    this.contact = this.contactOf(customer);

    this.patchPristine(this.contactForm, this.contactFormValues(customer, this.contact));
    this.patchPristine(this.accountForm, this.accountValues(customer));
    if (this.contactForm.get('country')?.pristine && this.contactForm.get('phone')?.pristine) {
      this.applyPhone(this.contactForm, this.contact?.phone);
    }
    this.syncPhoneRequirement(this.contact);

    const stored = Object.fromEntries((customer?.customFields ?? [])
      .map((item) => [`${(item as { _id?: string })._id}`, item?.value ?? null]));
    this.patchPristine(this.customFieldsGroup, stored);
    this.bindQualificationFields();
  }

  /**
   * A promotion changes the person behind the account, not the account (RF-03): only the
   * primary's own fields follow the new primary. The account's name, its fields, the deal
   * dates and the qualification keep what the user typed.
   */
  private applyPromoted(customer: Customer): void {
    this.customer = customer;
    this.contact = this.contactOf(customer);
    this.applyPerson(this.contact, this.personValues(customer, this.contact));
  }

  private loadLists(): void {
    this.companyService.getCrmLists()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (lists) => this.lists = lists ?? this.lists });
  }

  private loadQualificationFields(): void {
    this.customFieldService.getList(undefined, CustomFieldScopeEnum.CUSTOMER)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fields) => {
          this.qualificationFields = fields ?? [];
          this.bindQualificationFields();
        },
      });
  }

  /** One control per qualification field, seeded with the value the account already holds (RF-18). */
  private bindQualificationFields(): void {
    if (!this.accountForm) return;
    const group = this.customFieldsGroup;
    const stored = this.customer?.customFields ?? this.data?.customer?.customFields ?? [];

    this.qualificationFields.forEach((field) => {
      if (group.contains(field._id)) return;
      const value = stored.find((item) => `${(item as { _id?: string })._id}` === `${field._id}`)?.value ?? null;
      group.addControl(field._id, new FormControl(value));
    });
  }

  onZipBlur(): void {
    const zip = `${this.accountForm.get('address.zip')?.value ?? ''}`.replace(/\D/g, '');
    if (zip.length !== 8) return;

    // Same lookup the contact form uses; every filled field stays editable (RF-08).
    this.contactService.searchCep(zip)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => this.accountForm.get('address')?.patchValue({
          street: res?.street ?? '',
          neighborhood: res?.neighborhood ?? '',
          city: res?.city ?? '',
          state: res?.state ?? '',
        }),
        error: () => undefined,
      });
  }

  // ---------------------------------------------------------------------------
  // Create mode — the primary picked from the base fills the account's name and phone
  // ---------------------------------------------------------------------------

  onContactSelected(contact: Contact): void {
    this.selectedContact = contact;
    this.applyPerson(contact, this.contactFormValues(null, contact));
  }

  removeSelectedContact(): void {
    this.selectedContact = null;
    this.applyPerson(null, this.contactFormValues(null, null));
  }

  // ---------------------------------------------------------------------------
  // Linked contacts (RF-01..RF-03) — written on the spot when editing, queued when creating
  // ---------------------------------------------------------------------------

  /** A person typed in the inline form. */
  addContact(): void {
    if (this.newContactForm.invalid || this.savingContact || (!this.isNew && !this.customer?._id)) {
      this.newContactForm.markAllAsTouched();
      return;
    }

    const value = this.newContactForm.value;
    const person = <Contact>{
      name: value.name,
      phone: this.joinPhone(value.country, value.phone),
      email: value.email || undefined,
      contactPosition: value.contactPosition || undefined,
    };

    if (this.isNew) {
      this.queueContact({ contact: person, role: value.role });
      return;
    }
    this.linkContact({ ...person, role: value.role });
  }

  /**
   * A person picked from the base. When creating, the first pick becomes the primary and
   * fills the account's name and phone (owner rule); the next ones are queued as linked
   * contacts. The API links by id, so an existing person is never duplicated.
   */
  onLinkedContactSelected(contact: Contact): void {
    if (this.isNew && !this.selectedContact) {
      this.onContactSelected(contact);
      this.closeContactForm();
      return;
    }
    if (this.isNew) {
      this.queueContact({ contact, role: CustomerContactRoleEnum.OTHER });
      return;
    }
    this.linkContact({ contactId: contact._id, role: CustomerContactRoleEnum.OTHER });
  }

  private linkContact(body: AddContactBody): void {
    if (!this.customer?._id || this.savingContact) return;
    this.savingContact = true;

    this.customerService.addContact(this.customer._id, body)
      .pipe(takeUntil(this.destroy$), finalize(() => this.savingContact = false))
      .subscribe({
        next: (customer) => {
          this.customer = customer;
          this.closeContactForm();
          this.alertService.success(this.t('kanban.leadModal.contactAdded'));
        },
        error: (error) => this.alertService.error(error?.error?.message ?? this.t('kanban.customerFormModal.saveError')),
      });
  }

  /** Creation: the API skips the primary and repeated people; refusing them here gives the feedback the API would not. */
  private queueContact(entry: CustomerContact): void {
    const primaryPhone = this.joinPhone(this.contactForm.value.country, this.contactForm.value.phone);
    const same = (a: Contact, b: Contact) => (!!a._id && a._id === b._id) || (!!a.phone && a.phone === b.phone);

    const known = (this.selectedContact && same(entry.contact, this.selectedContact))
      || entry.contact.phone === primaryPhone
      || this.pendingContacts.some((pending) => same(entry.contact, pending.contact));

    if (known) {
      this.alertService.warning(this.t('kanban.leadModal.contactAlreadyOnLead'));
      return;
    }

    this.pendingContacts = [...this.pendingContacts, entry];
    this.closeContactForm();
  }

  /** Hides the inline form and the picker, whether a person was added or the user gave up. */
  closeContactForm(): void {
    this.addingContact = false;
    this.pickingContact = false;
    this.newContactForm.reset({ role: CustomerContactRoleEnum.OTHER, country: null });
  }

  removeContact(entry: CustomerContact): void {
    if (this.isNew) {
      this.pendingContacts = this.pendingContacts.filter((pending) => pending !== entry);
      return;
    }
    if (!this.customer?._id || this.savingContact) return;
    this.savingContact = true;

    this.customerService.removeContact(this.customer._id, `${entry.contact._id}`)
      .pipe(takeUntil(this.destroy$), finalize(() => this.savingContact = false))
      .subscribe({
        next: (customer) => {
          this.customer = customer;
          this.alertService.success(this.t('kanban.leadModal.contactRemoved'));
        },
        error: (error) => this.alertService.error(error?.error?.message ?? this.t('kanban.customerFormModal.saveError')),
      });
  }

  /** The promoted person becomes the messaging identity; the form follows the new primary (RF-03). */
  promoteContact(entry: CustomerContact): void {
    if (!this.customer?._id || this.savingContact) return;
    this.savingContact = true;

    this.customerService.promoteContact(this.customer._id, `${entry.contact._id}`)
      .pipe(takeUntil(this.destroy$), finalize(() => this.savingContact = false))
      .subscribe({
        next: (customer) => {
          this.applyPromoted(customer);
          this.alertService.success(this.t('kanban.leadModal.contactPromoted'));
        },
        error: (error) => this.alertService.error(error?.error?.message ?? this.t('kanban.customerFormModal.saveError')),
      });
  }

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  save(): void {
    if (this.contactForm.invalid || this.accountForm.invalid) {
      this.validateAllFormFields(this.contactForm);
      this.validateAllFormFields(this.accountForm);
      return;
    }

    this.loading = true;
    // Assembled inside the stream: an error while building the writes then reaches `finalize`
    // and the alert below, instead of escaping and leaving the button spinning.
    const request = defer(() => this.isNew ? this.createRequest() : this.updateRequest());

    request
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe({
        next: () => {
          this.alertService.success(this.t('kanban.leadModal.saved'));
          this.dialogRef.close(true);
        },
        error: (error) => this.alertService.error(error?.error?.message ?? this.t('kanban.customerFormModal.saveError')),
      });
  }

  /**
   * New customer: one create through the single creation path — the API takes the picked
   * contact as primary or resolves one by the typed phone (created when none exists),
   * refuses a primary that already anchors another account, links the queued people and
   * lands the account fields — then the person's own fields, which live on the contact.
   */
  private createRequest(): Observable<unknown> {
    const person = this.contactForm.value;
    const body = <Customer>{
      ...this.accountPatch(),
      phone: this.joinPhone(person.country, person.phone),
      email: person.email || null,
      contact: this.selectedContact ?? null,
      contacts: this.pendingContacts,
    };
    // A picked primary shows its stored fields, as the edit does: only what the user changed is
    // written, the number included. A typed one may have found a person the form never showed.
    const personPatch = this.selectedContact ? this.changedPersonFields() : this.personPatch();

    return this.customerService.create(body).pipe(
      switchMap((created: Customer) => {
        const idContact = typeof created?.contact === 'string' ? created.contact : created?.contact?._id;
        return idContact && Object.keys(personPatch).length
          ? this.contactService.update(`${idContact}`, <Contact>personPatch)
          : of(created);
      }),
    );
  }

  /**
   * A typed primary's own fields — filled ones only: the phone may have found a person the form
   * never showed, so a create never blanks what they already had, and the number that found them
   * stays as stored. The e-mail travels here too: the API only uses the typed one for a person it creates.
   */
  private personPatch(): Partial<Contact> {
    const person = this.contactForm.value;
    const patch: Partial<Contact> = {};

    if (person.email) patch.email = person.email;
    if (person.contactPosition) patch.contactPosition = person.contactPosition;
    if (person.contactCompany) patch.contactCompany = person.contactCompany;
    if (person.city) patch.city = person.city;
    if (person.state) patch.state = person.state;

    return patch;
  }

  /**
   * The primary's fields the user changed — only those travel, and a cleared one travels too, so
   * emptying a field really empties it. Sending the untouched ones as well would, before the
   * account read returns, write the form's empty placeholders over the person's real data.
   */
  private changedPersonFields(): Partial<Contact> {
    const person = this.contactForm.value;
    const changed = (key: string) => !!this.contactForm.get(key)?.dirty;
    const patch: Record<string, unknown> = {};

    if (changed('country') || changed('phone')) patch['phone'] = this.joinPhone(person.country, person.phone);
    CustomerFormModalComponent.PERSON_FIELDS
      .filter((key) => key !== 'country' && key !== 'phone')
      .forEach((key) => {
        if (changed(key)) patch[key] = person[key] || null;
      });

    return patch as Partial<Contact>;
  }

  /** Edit: each destination is written only when its part of the form changed (spec A-10). */
  private updateRequest(): Observable<unknown> {
    const writes: Array<Observable<unknown>> = [];
    const personPatch = this.changedPersonFields();

    // The primary is an object once the account read returns; before it — or after it failed —
    // it is still the id the caller's copy carries, which is enough to write the person.
    const rawPrimary = this.customer?.contact as unknown;
    const idPrimary = this.contact?._id ?? (typeof rawPrimary === 'string' ? rawPrimary : undefined);

    if (idPrimary && Object.keys(personPatch).length) {
      writes.push(this.contactService.update(`${idPrimary}`, <Contact>personPatch));
    }

    writes.push(this.customerService.update(this.customer._id, <Customer>this.accountPatch()));

    if (this.card?._id && this.datesDirty()) {
      writes.push(this.cardService.update(this.card._id, <Card>{
        presentedAt: this.presentedAtValue(),
        includedAt: this.accountForm.value.includedAt ?? null,
      }));
    }

    return forkJoin(writes);
  }

  /** The account's own fields — never the mirrors (`phone`/`email`), which the API ignores anyway. */
  private accountPatch(): Partial<Customer> {
    const value = this.accountForm.value;
    return {
      name: this.contactForm.value.name?.trim() || null,
      code: value.code,
      legalName: value.legalName,
      tradeName: value.tradeName,
      tin: this.documentDigits,
      stateRegistration: value.stateRegistration,
      website: value.website,
      instagram: value.instagram,
      address: value.address,
      segment: value.segment,
      employeeBand: value.employeeBand,
      customFields: this.qualificationFields.map((field) => <CustomFieldContact & { _id: string }>{
        _id: field._id,
        name: field.name,
        description: field.description,
        type: field.type,
        value: this.customFieldsGroup.get(field._id)?.value ?? null,
      }),
    };
  }

  private datesDirty(): boolean {
    return ['presentedAt', 'presentedTime', 'includedAt'].some((key) => this.accountForm.get(key)?.dirty);
  }

  private presentedAtValue(): Date | null {
    const date = this.accountForm.value.presentedAt;
    if (!date) return null;
    const [hours, minutes] = `${this.accountForm.value.presentedTime ?? ''}`.split(':').map(Number);
    const combined = new Date(date);
    combined.setHours(hours || 0, minutes || 0, 0, 0);
    return combined;
  }

  private timeOf(date: Date): string {
    return `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
  }

  private joinPhone(country: string, phone: string): string {
    return `${country ?? ''}${phone ?? ''}`.replace(/^\+/, '');
  }

  private extractPhoneNumber(phone: string): string {
    const match = phone.match(/\s(.+)$/);
    let result = match ? match[1] : phone;
    if (result.startsWith('55')) result = result.substring(2);
    return result;
  }

  private extractCountryCode(phone: string): string {
    const match = phone.match(/^\+\d+/);
    if (match) return match[0];
    if (phone.startsWith('55')) return '+55';
    return '';
  }

  close(): void {
    this.dialogRef.close();
  }
}
